import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import {
  getOrganization,
  updateOrganization,
  createOrganization,
  deleteOrganization,
} from "@/services/organization.client";
import { getUploadUrl } from "@/services/upload.client";
import { OrganizationState } from "@/types/organization";
import { toast } from "sonner";
import { DEFAULT_ERROR_TITLE } from "@/lib/constants";
import { getActiveSubscriptions } from "@/services/subscription.client";
import {
  createOrganizationMember,
  deleteOrganizationMember,
  getOrganizationMembers,
  updateOrganizationMember,
} from "@/services/member.client";
import { Subscription } from "@/types/subscription";
import { Member } from "@/types/member";
import {
  createTaxExemption,
  getTaxExemption,
} from "@/services/tax-exemption.client";

const createNoopStorage = () => {
  return {
    getItem: () => {
      return Promise.resolve(null);
    },
    setItem: () => {
      return Promise.resolve();
    },
    removeItem: () => {
      return Promise.resolve();
    },
  };
};

const getStorage = () => {
  if (typeof window !== "undefined") {
    return localStorage;
  }
  return createNoopStorage();
};

export const useOrganizationStore = create<OrganizationState>()(
  persist(
    (set, get) => ({
      organization: null,
      members: [],
      subscriptions: [],
      taxExemption: null,
      isLoading: false,
      error: null,
      hydrated: false,

      createOrganization: async (name, logo) => {
        try {
          set({ isLoading: true, error: null });

          if (logo && typeof logo === "string" && logo.length > 5000000) {
            throw new Error("Logo image is too large. Maximum size is 5MB.");
          }

          const organization = await createOrganization(name, logo);

          set({
            organization,
            subscriptions: [],
            isLoading: false,
          });

          return organization;
        } catch (error: any) {
          toast.error(DEFAULT_ERROR_TITLE, {
            description:
              "Failed to create organization. Please try again later.",
          });

          set({
            error: String(error.message || "Failed to create organization"),
            isLoading: false,
          });

          throw new Error(error);
        }
      },

      createOrganizationMember: async (
        userId,
        organizationId,
        inviterId,
        inviteToken,
        role,
        status,
      ) => {
        try {
          set({ isLoading: true, error: null });

          const member = await createOrganizationMember(
            userId,
            organizationId,
            inviterId,
            inviteToken,
            role,
            status,
          );

          set({
            members: [...get().members, member],
            isLoading: false,
          });

          return member;
        } catch (error: any) {
          toast.error(DEFAULT_ERROR_TITLE, {
            description:
              "Failed to create organization member. Please try again later.",
          });

          set({
            error: String(
              error.message || "Failed to create organization member",
            ),
            isLoading: false,
          });

          throw new Error(error);
        }
      },

      createTaxExemption: async (data) => {
        try {
          set({ isLoading: true, error: null });

          const taxExemption = await createTaxExemption(data);

          set({ isLoading: false, taxExemption });

          return taxExemption;
        } catch (error: any) {
          toast.error(DEFAULT_ERROR_TITLE, {
            description:
              "Failed to create tax exemption. Please try again later.",
          });

          set({
            error: String(error.message || "Failed to create tax exemption"),
            isLoading: false,
          });

          throw new Error(error);
        }
      },

      deleteOrganization: async (id: string) => {
        try {
          set({ isLoading: true, error: null });

          await deleteOrganization(id);

          // If we're deleting the currently selected organization, remove the cookie
          if (typeof window !== "undefined" && get().organization?.id === id) {
            document.cookie = "lastSelectedOrganization=; path=/; max-age=0";
          }

          set({
            organization: null,
            members: [],
            subscriptions: [],
            isLoading: false,
          });
        } catch (error: any) {
          toast.error(DEFAULT_ERROR_TITLE, {
            description:
              "Failed to delete your organization. Please try again later.",
          });

          set({
            error: String(error.message || "Failed to delete organization"),
            isLoading: false,
          });

          throw new Error(error);
        }
      },

      deleteOrganizationMember: async (memberId, userId, organizationId) => {
        try {
          set({ isLoading: true, error: null });

          await deleteOrganizationMember(memberId, userId, organizationId);

          const updatedMembers = get().members.filter(
            (member) => member.id !== memberId,
          );

          set({
            members: updatedMembers,
            isLoading: false,
          });
        } catch (error: any) {
          toast.error(DEFAULT_ERROR_TITLE, {
            description:
              "Failed to delete organization member. Please try again later.",
          });

          set({
            error: String(
              error.message || "Failed to delete organization member",
            ),
            isLoading: false,
          });

          throw new Error(error);
        }
      },

      fetchOrganization: async (id: string, forceRefresh = false) => {
        try {
          set({ isLoading: true, error: null });

          const organization = await getOrganization(id, forceRefresh);

          if (!organization) throw new Error("Organization was not found");

          // Sign the logo URL if it's an S3 object
          if (organization.logo.includes("organizations/")) {
            organization.logo = await getUploadUrl(organization.logo);
          }

          const members: Member[] = await getOrganizationMembers(
            organization.id,
            forceRefresh,
          );

          let subscriptions: Subscription[] = [];
          if (organization.customerId) {
            subscriptions =
              (await getActiveSubscriptions(organization.customerId)) || [];
          }

          set({
            organization,
            members,
            subscriptions,
            isLoading: false,
          });

          if (typeof window !== "undefined") {
            document.cookie = `lastSelectedOrganization=${organization.id}; path=/; max-age=31536000`;
          }
        } catch (error: any) {
          toast.error(DEFAULT_ERROR_TITLE, {
            description: "Failed to fetch organization. Please try again later",
          });

          set({
            error: String(error.message || "Failed to fetch organization"),
            isLoading: false,
          });

          if (typeof window !== "undefined") {
            document.cookie = `lastSelectedOrganization=; path=/; max-age=31536000`;
          }

          throw new Error(error);
        }
      },

      getOrganizationMembers: async (organizationId, forceRefresh = false) => {
        try {
          set({ isLoading: true, error: null });

          const members = await getOrganizationMembers(
            organizationId,
            forceRefresh,
          );

          set({
            members,
            isLoading: false,
          });

          return members;
        } catch (error: any) {
          toast.error(DEFAULT_ERROR_TITLE, {
            description:
              "Failed to fetch organization members. Please try again later",
          });

          set({
            error: String(
              error.message || "Failed to fetch organization members",
            ),
            isLoading: false,
          });

          throw new Error(error);
        }
      },

      getTaxExemption: async (organizationId, forceRefresh = false) => {
        try {
          set({ isLoading: true, error: null });

          const taxExemption = await getTaxExemption(
            organizationId,
            forceRefresh,
          );

          set({ isLoading: false, taxExemption });

          return taxExemption;
        } catch (error: any) {
          toast.error(DEFAULT_ERROR_TITLE, {
            description:
              "Failed to fetch tax exemption. Please try again later",
          });

          set({
            error: String(error.message || "Failed to fetch tax exemption"),
            isLoading: false,
          });

          throw new Error(error);
        }
      },

      updateOrganization: async (data) => {
        try {
          set({ isLoading: true, error: null });

          if (
            data.logo &&
            typeof data.logo === "string" &&
            data.logo.length > 5000000
          ) {
            throw new Error("Logo image is too large. Maximum size is 5MB.");
          }

          const updatedOrganization = await updateOrganization(data);

          // Sign the logo URL if it's an S3 object
          if (updatedOrganization.logo.includes("organizations/")) {
            updatedOrganization.logo = await getUploadUrl(
              updatedOrganization.logo,
            );
          }

          set({
            organization: updatedOrganization,
            isLoading: false,
          });

          if (data.customerId) {
            const subscriptions = await getActiveSubscriptions(data.customerId);
            set({ subscriptions });
          }

          return updatedOrganization;
        } catch (error: any) {
          toast.error(DEFAULT_ERROR_TITLE, {
            description:
              "Failed to update organization. Please try again later.",
          });

          set({
            error: String(error.message || "Failed to update organization"),
            isLoading: false,
          });

          throw new Error(error);
        }
      },

      updateOrganizationMember: async (data) => {
        try {
          set({ isLoading: true, error: null });

          const updatedMember = await updateOrganizationMember(data);

          set({
            members: [
              ...get().members.map((member) =>
                member.id === updatedMember.id ? updatedMember : member,
              ),
            ],
            isLoading: false,
          });

          return updatedMember;
        } catch (error: any) {
          toast.error(DEFAULT_ERROR_TITLE, {
            description:
              "Failed to update organization member. Please try again later.",
          });

          set({
            error: String(
              error.message || "Failed to update organization member",
            ),
            isLoading: false,
          });

          throw new Error(error);
        }
      },

      setError: (error) => {
        set({ error });
      },

      setOrganization: (organization) => {
        set({ organization });
      },

      setMembers: (members) => {
        set({ members });
      },

      setSubscriptions: (subscriptions) => {
        set({ subscriptions });
      },

      setTaxExemption: (taxExemption) => {
        set({ taxExemption });
      },

      setHydrated: (hydrated) => {
        set({ hydrated });
      },
    }),
    {
      name: "libum-organization-storage",
      storage: createJSONStorage(() => getStorage()),
      partialize: (state) => ({ organization: state.organization }),
      skipHydration: typeof window === "undefined",
      onRehydrateStorage: () => (state) => {
        if (state) {
          state.setHydrated(true);
        }
      },
    },
  ),
);
